import typewriter, {
  type ViolationHandler,
} from '@/analytics/generated/segment'
import { AnalyticsBrowser, AnalyticsSnippet } from '@segment/analytics-next'
import {
  isAnalyticsDevLoggingEnabled,
  logAnalyticsEventPayload,
  missingEventPropertiesPlugin,
} from './interceptors/missingEventProperties'
import { sessionIdPlugin } from './interceptors/sessionId'

type TypewriterSchemaViolation = {
  keyword?: string
  message?: string
  instancePath?: string
}

const getTypewriterEventName = (message: {
  [key: string]: unknown
}): string => {
  const { event } = message

  return typeof event === 'string' ? event : 'unknown'
}

const getTypewriterValidationIssues = (
  violations: TypewriterSchemaViolation[]
): string[] =>
  violations
    .filter(violation => violation.keyword !== 'required')
    .map(violation => {
      const path = violation.instancePath
        ?.replace(/^\//, '')
        .replace(/\//g, '.')

      if (path && violation.message) {
        return `${path}: ${violation.message}`
      }

      return violation.message ?? violation.keyword ?? 'validation_error'
    })

const handleTypewriterViolation: ViolationHandler = (message, violations) => {
  if (!isAnalyticsDevLoggingEnabled()) {
    return
  }

  const typedViolations = violations as TypewriterSchemaViolation[]
  const issues = getTypewriterValidationIssues(typedViolations)

  if (issues.length === 0) {
    return
  }

  const typedMessage = message as { [key: string]: unknown }
  const eventName = getTypewriterEventName(typedMessage)

  logAnalyticsEventPayload(eventName, typedMessage)

  console.warn(`[analytics] "${eventName}" has validation issues:`, issues)
}

export const segmentClient = typewriter

export const segmentEventWithTypewriter = {
  track: (eventName: string, properties: { [key: string]: unknown }) => {
    return sendAnalyticsEvent(eventName, properties)
  },
} as AnalyticsSnippet

const writeKey = process.env.SEGMENT_WRITE_KEY ?? ''

const segmentAnalyticsPlugins = [missingEventPropertiesPlugin, sessionIdPlugin]

type SegmentAnalyticsClient = ReturnType<typeof AnalyticsBrowser.load>

const createSegmentAnalyticsClient = (): SegmentAnalyticsClient =>
  AnalyticsBrowser.load({
    writeKey,
    plugins: segmentAnalyticsPlugins,
  })

const hotData = import.meta.hot?.data as {
  segmentAnalyticsClient?: SegmentAnalyticsClient
}

const getSegmentAnalyticsClient = (): SegmentAnalyticsClient => {
  if (hotData?.segmentAnalyticsClient) {
    return hotData.segmentAnalyticsClient
  }

  const analyticsClient = createSegmentAnalyticsClient()

  if (hotData) {
    hotData.segmentAnalyticsClient = analyticsClient
  }

  return analyticsClient
}

export const client = getSegmentAnalyticsClient()

export const sendAnalyticsEvent = (
  eventName: string,
  properties: { [key: string]: unknown }
) => {
  return client.track(eventName, { ...properties })
}

segmentClient.setTypewriterOptions({
  analytics: segmentEventWithTypewriter,
  onViolation: handleTypewriterViolation,
})
